'use client';

import clsx from 'clsx';
import type { FC } from 'react';
import type { CtaProps } from '@/types/cta';
import type { CardProps } from '@/types/card';
import { Card } from '@/components/raven/Card';
import Autoplay from 'embla-carousel-autoplay';
import { SurferLogo } from '@/assets/surfer/logo';
import useEmblaCarousel from 'embla-carousel-react';
import { LinkWrapper } from '@/components/LinkWrapper';
import CaretOpen from '@themaven-net/raven-assets/CaretOpen';
import { imageSizes } from '@/components/raven/Card/image-sizes';
import { DotButton } from '@/components/raven/content/Carousels/Controls';
import { useCarouselControls } from '@/utils/hooks/use-carousel-controls';

import styles from './styles.module.css';

interface HeroSlideProps {
	active: boolean;
	article: CardProps;
	index: number;
}

const HeroSlide: FC<HeroSlideProps> = ({ active, article }) => {
	const cx = clsx(styles.carouselHeroSlide, 'carousel__slide', {
		[styles.carouselHeroSlideActive]: active,
	});

	return (
		<div className={cx}>
			<Card
				{...article}
				sizeOverride={imageSizes['hero-carousel']}
				tabIndex={active ? 0 : -1}
				titleTag={article.titleTag ?? 'h2'}
				variation="overlay-hero"
			/>
		</div>
	);
};

interface CarouselHeroProps {
	autoRotate?: boolean;
	className?: string;
	cta?: CtaProps;
	delay?: number;
	posts: CardProps[];
}

export const CarouselHero: FC<CarouselHeroProps> = ({
	autoRotate = false,
	className,
	cta,
	delay = 5,
	posts,
}) => {
	const cx = clsx(styles.carouselHero, 'is-dark', 'avoid-margin', className);

	// This is specific to the Surfer Site. DO NOT REMOVE.
	const isSurferHero = className?.includes('is-surfer-hero');

	const [carouselRef, carouselApi] = useEmblaCarousel(
		{
			align: 'start',
			loop: true,
			slidesToScroll: 1,
			startIndex: 0,
		},
		[Autoplay({ delay: delay * 1000, playOnInit: autoRotate })],
	);

	const { onDotButtonClick, scrollSnaps, selectedIndex } =
		useCarouselControls(carouselApi);

	if (!posts.length) {
		return null;
	}

	return (
		<div className={cx}>
			<div className={styles.carouselHeroWrapper}>
				<div className="carousel">
					<div className="carousel__viewport" ref={carouselRef}>
						<div
							className={clsx(
								styles.carouselHeroWrapperContainer,
								'carousel__container',
							)}
						>
							{posts.map((article, index) => (
								<HeroSlide
									active={index === selectedIndex}
									article={article}
									index={index}
									key={article.id}
								/>
							))}
						</div>
					</div>

					<div
						className={clsx(styles.carouselHeroControls, 'carousel__controls')}
					>
						<ul className="carousel__dots">
							{scrollSnaps.map((_, index) => (
								// eslint-disable-next-line react/no-array-index-key
								<li key={`dot-${scrollSnaps[index]}-${index}`}>
									<DotButton
										active={index === selectedIndex}
										aria-label={`Go to slide ${index + 1}`}
										onClick={() => onDotButtonClick(index)}
									/>
								</li>
							))}
						</ul>
					</div>
				</div>
				{cta && cta.url && cta.title && (
					<div className={styles.carouselHeroCta}>
						<LinkWrapper
							className={styles.carouselHeroCtaButton}
							href={cta.url}
						>
							{cta.title}
							<CaretOpen height={12} width={12} />
						</LinkWrapper>
					</div>
				)}
			</div>
			{isSurferHero && (
				<div className={styles.carouselHeroSurferLogo}>
					<SurferLogo />
				</div>
			)}
		</div>
	);
};
